import {Injectable} from "@angular/core";
import {environment} from "../../../environments/environment";
import {arrayRemove, arrayUpsert} from "@datorama/akita";
import {HttpClient, HttpParams} from "@angular/common/http";
import {AuthService} from "@auth0/auth0-angular";
import {
  createInitialCustomerCriteria,
  CustomerState,
  CustomerStore
} from "./customer.store";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  first,
  map,
  Observable,
  of,
  ReplaySubject,
  Subject,
  switchMap,
  tap,
  timeout
} from "rxjs";
import {
  Customer,
  CustomerCriteria,
  CustomerIndex,
  CustomerListItem
} from "../../data-access/api-customers/Customer";
import {retryBackoff} from "../../data-access/retry.service";
import {PageResponse} from "../../data-access/PageResponse";


@Injectable({ providedIn: 'root' })
export class CustomerStoreService {
  private readonly baseUrl: string;
  private apiV1: string = environment.apiV1;
  private baseRef: string = environment.origin;
  private serviceCore: string = environment.service.core;

  /**
   * Source unique des critères de la liste. `ReplaySubject` et non `BehaviorSubject` : rien
   * n'est chargé avant que des critères réels soient poussés, ce qui évite une requête
   * parasite au montage (même raison que pour la sélection de résidence du dashboard,
   * cf. DECISIONS.md du 2026-10-01).
   */
  private readonly criteria$: ReplaySubject<CustomerCriteria>
    = new ReplaySubject<CustomerCriteria>(1);

  /** Le debounce ne porte que sur la frappe : un changement de page doit rester immédiat. */
  private readonly searchTerm$: Subject<string>
    = new Subject<string>();

  /** Terme de l'autocomplétion de sélection d'un client, indépendant de celui de la liste. */
  private readonly selectTerm$: Subject<string>
    = new Subject<string>();

  /** Les écouteurs ci-dessous vivent le temps de la session : ne jamais les installer deux fois. */
  private listening: boolean = false;

  constructor(
    private store: CustomerStore,
    private httpClient: HttpClient,
    private authService: AuthService,
  ) {
    this.baseUrl = environment.local;
  }

  public setLoading(isLoading: boolean): void {
    this.store.setLoading(isLoading);
  }

  public init(): void {
    this.authService.isLoading$
      .pipe(
        // wait until SDK finished init/callback
        filter((loading: boolean) => !loading),
        switchMap(() => this.authService.isAuthenticated$),
        first(),
        // safe: token is ready, interceptor will attach it
        switchMap((isAuth: boolean) => {
          if (!isAuth) {
            this.authService.loginWithRedirect({ appState: { target: '/services'} });
            return EMPTY;
          }
          if (!this.listening) {
            this.listening = true;
            this.listenToCriteria();
            this.listenToSearchTerm();
            this.listenToSelectTerm();
          }
          // Akita démarre un store avec `loading: true`. Sans cette remise à zéro, les écrans
          // branchés sur `isLoading$` resteraient en chargement toute la session : le pipeline
          // ci-dessus se termine sur EMPTY et ne repasserait jamais par un `finalize`.
          // Au-delà, c'est `listenToCriteria` qui pilote l'indicateur, requête par requête.
          this.setLoading(false);
          return EMPTY;
        }),
        catchError((err) => {
          console.error('[bootstrap customers] init failed', err);
          this.setLoading(false);
          return EMPTY;
        })
      ).subscribe();
  }

  /**
   * Un seul abonnement pour toute la session : `switchMap` annule la requête encore en vol
   * dès que les critères changent, ce qui supprime les réponses qui arrivent dans le désordre
   * pendant que l'utilisateur tape ou enchaîne les pages.
   */
  private listenToCriteria(): void {
    this.criteria$
      .pipe(
        distinctUntilChanged((left: CustomerCriteria, right: CustomerCriteria) =>
          this.criteriaKey(left) === this.criteriaKey(right)),
        tap(() => this.setLoading(true)),
        switchMap((criteria: CustomerCriteria) => this.fetchPage(criteria)
          .pipe(
            timeout({ first: 15000 }),
            retryBackoff(1, 400),
            tap((res: PageResponse<CustomerListItem>) => {
              this.store.update((state: CustomerState) => ({
                ...state,
                customers: res.items ? [ ...res.items ] : [],
                customerCriteria: { ...criteria },
                customerTotalItems: res.totalItems,
                customerTotalPage: res.totalPages,
                error: undefined
              }));
            }),
            catchError((error) => {
              this.store.setError(error);
              return of(null);
            })
          )),
        tap(() => this.setLoading(false))
      )
      .subscribe();
  }

  private listenToSearchTerm(): void {
    this.searchTerm$
      .pipe(
        debounceTime(350),
        distinctUntilChanged()
      )
      .subscribe((term: string) => this.patchCriteria({ term, page: 0 }));
  }

  /**
   * Point d'entrée des onglets : fixe les critères propres à l'onglet et déclenche le premier
   * chargement. Remet la pagination à la première page.
   *
   * Ne charge rien d'autre que la page : les widgets et les listes déroulantes ont leurs
   * propres appels, ciblés (`loadLatest`, `searchForSelect`).
   */
  public loadFirstPage(criteria: Partial<CustomerCriteria>): void {
    this.criteria$.next({
      ...createInitialCustomerCriteria(),
      ...criteria,
      page: 0
    });
  }

  /** Recherche serveur ; le terme est debouncé avant d'atteindre le réseau. */
  public search(term: string): void {
    this.searchTerm$.next(term ? term.trim() : '');
  }

  public goToPage(pageIndex: number): void {
    // nz-table indexe les pages à partir de 1, l'API à partir de 0.
    this.patchCriteria({ page: Math.max(0, pageIndex - 1) });
  }

  public changePageSize(size: number): void {
    this.patchCriteria({ size, page: 0 });
  }

  public changeSort(sort: string): void {
    this.patchCriteria({ sort, page: 0 });
  }

  private patchCriteria(patch: Partial<CustomerCriteria>): void {
    const current: CustomerCriteria = this.store.getValue().customerCriteria;
    this.criteria$.next({ ...current, ...patch });
  }

  private fetchPage(criteria: CustomerCriteria): Observable<PageResponse<CustomerListItem>> {
    const url: string = `${this.baseRef}${this.serviceCore}${this.apiV1}customers/search`;

    let httpParams: HttpParams = new HttpParams()
      .set('page', String(criteria.page))
      .set('size', String(criteria.size))
      .set('sort', criteria.sort);

    if (criteria.term) {
      httpParams = httpParams.set('q', criteria.term);
    }
    if (criteria.enabled !== null) {
      httpParams = httpParams.set('enabled', String(criteria.enabled));
    }
    if (criteria.resident !== null) {
      httpParams = httpParams.set('resident', String(criteria.resident));
    }
    if (criteria.residenceId) {
      httpParams = httpParams.set('residenceId', criteria.residenceId);
    }
    if (criteria.type) {
      httpParams = httpParams.set('type', criteria.type);
    }

    return this.httpClient.get<PageResponse<CustomerListItem>>(url, { params: httpParams });
  }

  private criteriaKey(criteria: CustomerCriteria): string {
    return [
      criteria.term,
      criteria.enabled,
      criteria.resident,
      criteria.residenceId,
      criteria.type,
      criteria.page,
      criteria.size,
      criteria.sort
    ].join('|');
  }

  /**
   * Derniers occupants créés, pour les widgets du dashboard. Quelques lignes seulement :
   * télécharger tout le référentiel coûte ~620 Ko compressés pour 20 000 fiches, ce qui est
   * inacceptable sur une connexion dégradée.
   */
  public loadLatest(residenceId: string | null, limit: number = 10): void {
    let httpParams: HttpParams = new HttpParams().set('limit', String(limit));
    if (residenceId) {
      httpParams = httpParams.set('residenceId', residenceId);
    }

    this.httpClient.get<CustomerIndex[]>(
      `${this.baseRef}${this.serviceCore}${this.apiV1}customers/latest`,
      { params: httpParams }
    )
      .pipe(
        timeout({ first: 10000 }),
        retryBackoff(1, 400),
        tap((customers: CustomerIndex[]) => {
          this.store.update((state: CustomerState) => ({
            ...state,
            latestCustomers: customers ? [ ...customers ] : []
          }));
        }),
        catchError((error) => {
          // Widget non critique : on n'affiche pas d'erreur bloquante pour ça.
          console.error('[customers latest] load failed', error);
          return EMPTY;
        })
      )
      .subscribe();
  }

  /**
   * Autocomplétion serveur pour les listes déroulantes de sélection d'un client. Remplace le
   * chargement de tout le référentiel : on ne ramène qu'une vingtaine de correspondances par
   * frappe. En dessous de deux caractères, on ne cherche pas.
   */
  public searchForSelect(term: string): void {
    this.selectTerm$.next(term ? term.trim() : '');
  }

  private listenToSelectTerm(): void {
    this.selectTerm$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term: string) => {
          if (!term || term.length < 2) {
            this.store.update((state: CustomerState) => ({ ...state, selectCustomers: [] }));
            return EMPTY;
          }

          return this.fetchPage({
            ...createInitialCustomerCriteria(),
            term,
            enabled: true,
            size: 20,
            sort: 'customerFullName,asc'
          }).pipe(
            timeout({ first: 10000 }),
            tap((res: PageResponse<CustomerListItem>) => {
              this.store.update((state: CustomerState) => ({
                ...state,
                selectCustomers: res.items ? [ ...res.items ] : []
              }));
            }),
            catchError((error) => {
              console.error('[customers select] search failed', error);
              return EMPTY;
            })
          );
        })
      )
      .subscribe();
  }

  /** Recharge la page courante, après une création ou une suppression par exemple. */
  public reloadCurrentPage(): void {
    const current: CustomerCriteria = this.store.getValue().customerCriteria;
    this.criteria$.next({ ...current });
  }

  /**
   * UPSERT & DELETE METHODS
   *
   *
   *
   */

  /**
   * Répercute une écriture sur la page courante et sur l'index. Le store ne détenant plus que
   * les colonnes de la liste, le `Customer` complet renvoyé par l'API est projeté ici.
   */
  public upsertCustomer(customer: Customer): void {
    if (!customer || !customer.id) {
      return;
    }

    this.store.update((state: CustomerState) => {
      const isInCurrentPage: boolean = state.customers
        .some((item: CustomerListItem) => item.id === customer.id);

      return {
        ...state,
        customers: isInCurrentPage
          ? arrayUpsert(state.customers, customer.id, this.toListItem(customer))
          : state.customers,
        latestCustomers: state.latestCustomers
          .some((item: CustomerIndex) => item.id === customer.id)
          ? arrayUpsert(state.latestCustomers, customer.id, this.toIndex(customer))
          : state.latestCustomers
      };
    });
  }

  public async deleteCustomer(customerId: string): Promise<boolean> {
    return new Promise<boolean>((resolve => {
      this.store.update((state: CustomerState) => ({
        ...state,
        customers: arrayRemove(state.customers, customerId),
        latestCustomers: arrayRemove(state.latestCustomers, customerId),
        selectCustomers: arrayRemove(state.selectCustomers, customerId)
      }));
      resolve(true);
    }));
  }

  private toListItem(customer: Customer): CustomerListItem {
    return {
      id: customer.id,
      dateAdd: customer.dateAdd,
      dateUpd: customer.dateUpd,
      enabled: customer.enabled,
      externalId: customer.externalId,
      ceriId: customer.ceriId,
      customerFullName: customer.customerFullName,
      customerEmail: customer.customerEmail,
      customerType: customer.customerType,
      customerNationality: customer.customerNationality,
      customerImageUrl: customer.customerImageUrl,
      customerResidenceId: customer.customerResidenceId,
    };
  }

  private toIndex(customer: Customer): CustomerIndex {
    return {
      id: customer.id,
      dateAdd: customer.dateAdd,
      externalId: customer.externalId,
      ceriId: customer.ceriId,
      customerFullName: customer.customerFullName,
      customerEmail: customer.customerEmail,
      customerType: customer.customerType,
      customerImageUrl: customer.customerImageUrl,
      customerResidenceId: customer.customerResidenceId,
    };
  }

}
